import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Button, Field, Notice, Select } from "../components";
import { Link } from "../router";
import { useTenant } from "../shell";
import { ID_LABEL, PURPOSE_LABEL, SEX_LABEL, STATUS_LABEL } from "./patientLabels";
import { PatientAppointments } from "./Appointments";
import { PatientTimeline } from "./Timeline";
import { PatientLabResults } from "./Lab";

interface Consent { purpose: string; status: string; at: string | null; method: string | null; givenBy: string | null }
interface Template { id: string; purpose: string; title: string; body: string; status: string; version: number }
const fmt = (iso: string) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
/** A calendar date (no time) is formatted from its digits, never converted between time zones. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d: string) => `${d.slice(8, 10)} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;

export function PatientProfile({ id }: { id: string }) {
  const { can, branchId } = useTenant();
  const [p, setP] = useState<any>(null);
  const [canReveal, setCanReveal] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [consents, setConsents] = useState<Consent[] | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);

  const load = useCallback(() => {
    api.authed(`/api/v1/patients/${id}`).then((r) => { setP(r.patient); setCanReveal(r.canReveal); setRevealed({}); }).catch((e) => setError(e.message));
    if (can("consent:view")) {
      api.authed(`/api/v1/patients/${id}/consents`).then((r) => setConsents(r.summary)).catch(() => {});
      api.authed("/api/v1/consent-templates").then((r) => setTemplates(r.items)).catch(() => {});
    }
  }, [id, can]);
  useEffect(load, [load]);

  async function reveal(field: string) {
    try { const r = await api.authed(`/api/v1/patients/${id}/reveal`, { body: { field } }); setRevealed((x) => ({ ...x, [field]: r.value })); }
    catch (e) { setError((e as ApiError).message); }
  }
  if (error && !p) return <Notice tone="error">{error}</Notice>;
  if (!p) return <p className="muted">Loading…</p>;

  const phone = (field: string, masked: string | null) => masked ? (
    <span className="reveal">
      {revealed[field] ? <a href={`tel:${revealed[field]}`} className="mono">{revealed[field]}</a> : <span className="mono">{masked}</span>}
      {canReveal && !revealed[field] && <button type="button" className="link-button" onClick={() => reveal(field)}>Show</button>}
    </span>) : <span className="muted">Not recorded</span>;

  return (
    <>
      {p.status === "merged" && <Notice tone="info">This record was merged. <Link to={`/app/patients/${p.mergedInto}`}>Open the current record</Link>.</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      <header className="patient-banner">
        <div>
          <h1>{p.fullName}</h1>
          <p className="banner-meta"><span>{p.age}</span><span>{SEX_LABEL[p.sex]}</span>{p.bloodGroup && <span>Blood group {p.bloodGroup}</span>}</p>
        </div>
        <div className="uhid-block"><span className="muted">UHID</span><strong className="mono" data-testid="uhid">{p.uhid}</strong></div>
      </header>
      <div className="actions-row">
        {can("patient:edit") && p.status === "active" && <Link to={`/app/patients/${id}/edit`} className="btn btn-secondary">Edit details</Link>}
      </div>

      {can("appointment:view") && p.status === "active" && <PatientAppointments patientId={id} />}

      <section aria-labelledby="contact-h">
        <h2 id="contact-h">Contact</h2>
        <dl className="kv">
          <div><dt>Mobile</dt><dd>{phone("mobile", p.mobile)}</dd></div>
          {p.alternateMobile && <div><dt>Alternate mobile</dt><dd>{phone("alternateMobile", p.alternateMobile)}</dd></div>}
          <div><dt>Email</dt><dd>{p.email ? (revealed.email ?? p.email) : <span className="muted">Not recorded</span>}{p.email && canReveal && !revealed.email && <button type="button" className="link-button" onClick={() => reveal("email")}>Show</button>}</dd></div>
          <div><dt>Address</dt><dd>{[p.address.line1, p.address.city, p.address.pin].filter(Boolean).join(", ") || <span className="muted">Not recorded</span>}</dd></div>
          {p.guardian && <div><dt>Parent / guardian</dt><dd>{p.guardian.name} ({p.guardian.relation}) {p.guardian.mobile && phone("guardianMobile", p.guardian.mobile)}</dd></div>}
          {p.emergencyContact && <div><dt>Emergency contact</dt><dd>{p.emergencyContact.name} ({p.emergencyContact.relation}) {p.emergencyContact.mobile && phone("emergencyMobile", p.emergencyContact.mobile)}</dd></div>}
        </dl>
      </section>

      <section aria-labelledby="id-h">
        <h2 id="id-h">Identity</h2>
        <dl className="kv">
          <div><dt>Date of birth</dt><dd>{p.dateOfBirth ? (p.dobEstimated ? `About ${p.age} (estimated)` : fmtDate(p.dateOfBirth)) : "Not known"}</dd></div>
          <div><dt>ABHA number</dt><dd>{p.abhaNumber ? (revealed.abhaNumber ?? p.abhaNumber) : <span className="muted">Not recorded</span>}{p.abhaNumber && canReveal && !revealed.abhaNumber && <button type="button" className="link-button" onClick={() => reveal("abhaNumber")}>Show</button>}</dd></div>
          {p.identifiers.map((i: { kind: string; last4: string }) => <div key={i.kind}><dt>{ID_LABEL[i.kind]}</dt><dd className="mono">••••{i.last4}</dd></div>)}
          <div><dt>Registered</dt><dd>{fmt(p.createdAt)}</dd></div>
        </dl>
      </section>

      {consents && <ConsentSection patientId={id} isMinor={p.isMinor} consents={consents} templates={templates} canRecord={can("consent:create") && !!branchId && p.status === "active"} onChange={load} />}
      {p.status === "active" && <PatientLabResults patientId={id} />}
      {p.status === "active" && <PatientTimeline patientId={id} />}
      {can("patient_merge:create") && p.status === "active" && <ReportDuplicate keepId={id} keepUhid={p.uhid} />}
    </>
  );
}

function ConsentSection({ patientId, isMinor, consents, templates, canRecord, onChange }:
  { patientId: string; isMinor: boolean; consents: Consent[]; templates: Template[]; canRecord: boolean; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState({ purpose: "communication", action: "granted", method: "written", givenBy: isMinor ? "guardian" : "patient", givenByName: "", givenByRelation: "", witnessName: "" });
  const [msg, setMsg] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const tpl = templates.filter((t) => t.purpose === c.purpose && t.status === "active").sort((a, b) => b.version - a.version)[0];
  const set = (k: keyof typeof c) => (e: { target: { value: string } }) => setC((x) => ({ ...x, [k]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault(); setMsg(null); setBusy(true);
    try {
      await api.authed(`/api/v1/patients/${patientId}/consents`, { body: { ...c, templateId: c.action === "granted" ? tpl?.id : null } });
      setMsg({ tone: "success", text: `Saved: ${PURPOSE_LABEL[c.purpose]}, ${STATUS_LABEL[c.action]?.toLowerCase()}.` }); setOpen(false); onChange();
    } catch (ex) { setMsg({ tone: "error", text: (ex as ApiError).message }); } finally { setBusy(false); }
  }

  return (
    <section aria-labelledby="consent-h">
      <h2 id="consent-h">Consent</h2>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <table className="data">
        <thead><tr><th scope="col">Purpose</th><th scope="col">Status</th><th scope="col">Recorded</th></tr></thead>
        <tbody>{consents.map((x) => (
          <tr key={x.purpose}><td>{PURPOSE_LABEL[x.purpose]}</td><td><span className={`status s-${x.status}`}>{STATUS_LABEL[x.status]}</span></td>
            <td className="muted">{x.at ? `${fmt(x.at)}, ${x.method?.replace("_", " ")}${x.givenBy && x.givenBy !== "patient" ? ` by ${x.givenBy}` : ""}` : "—"}</td></tr>))}
        </tbody>
      </table>
      {canRecord && !open && <Button type="button" variant="secondary" onClick={() => setOpen(true)}>Record consent</Button>}
      {canRecord && open && (
        <form className="consent-form" onSubmit={save}>
          <div className="row">
            <Select label="Purpose" value={c.purpose} onChange={set("purpose")} options={Object.entries(PURPOSE_LABEL).map(([value, label]) => ({ value, label }))} />
            <Select label="Decision" value={c.action} onChange={set("action")} options={[{ value: "granted", label: "Gives consent" }, { value: "refused", label: "Refuses" }, { value: "withdrawn", label: "Withdraws consent" }]} />
          </div>
          {c.action === "granted" && (tpl ? <blockquote className="consent-text"><strong>{tpl.title}</strong> <span className="muted">(version {tpl.version})</span><p>{tpl.body}</p></blockquote>
            : <Notice tone="error">No consent text is published for this purpose yet.</Notice>)}
          <div className="row">
            <Select label="How it was given" value={c.method} onChange={set("method")} options={[{ value: "written", label: "Signed form" }, { value: "electronic", label: "On screen" }, { value: "verbal_witnessed", label: "Verbal, witnessed" }, { value: "otp", label: "OTP on mobile" }]} />
            <Select label="Given by" value={c.givenBy} onChange={set("givenBy")} options={[...(isMinor ? [] : [{ value: "patient", label: "Patient" }]), { value: "guardian", label: "Parent or guardian" }, { value: "legal_representative", label: "Legal representative" }]} />
          </div>
          {c.givenBy !== "patient" && <div className="row"><Field label="Their name" value={c.givenByName} onChange={set("givenByName")} /><Field label="Relationship" optional value={c.givenByRelation} onChange={set("givenByRelation")} /></div>}
          {c.method === "verbal_witnessed" && <Field label="Witness name" value={c.witnessName} onChange={set("witnessName")} />}
          <div className="form-actions"><Button type="submit" busy={busy}>Save consent</Button><Button type="button" variant="quiet" onClick={() => setOpen(false)}>Cancel</Button></div>
        </form>
      )}
    </section>
  );
}

function ReportDuplicate({ keepId, keepUhid }: { keepId: string; keepUhid: string }) {
  const [open, setOpen] = useState(false);
  const [uhid, setUhid] = useState(""), [reason, setReason] = useState("");
  const [msg, setMsg] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  async function send(e: FormEvent) {
    e.preventDefault(); setMsg(null); setBusy(true);
    try {
      const other = await api.authed("/api/v1/patients/lookup-uhid", { body: { uhid } });
      await api.authed("/api/v1/patient-merges", { body: { sourcePatientId: other.patient.id, targetPatientId: keepId, reason } });
      setMsg({ tone: "success", text: `Request sent. ${other.patient.uhid} will be merged into ${keepUhid} once another person approves it.` }); setOpen(false);
    } catch (ex) { setMsg({ tone: "error", text: (ex as ApiError).message }); } finally { setBusy(false); }
  }
  return (
    <section aria-labelledby="dup-h">
      <h2 id="dup-h">Duplicate record</h2>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      {!open ? <Button type="button" variant="quiet" onClick={() => setOpen(true)}>Report a duplicate of this patient</Button> : (
        <form onSubmit={send} className="consent-form">
          <p className="hint">This record ({keepUhid}) is kept. The other record is merged into it after a second person approves.</p>
          <Field label="UHID of the duplicate record" value={uhid} onChange={(e) => setUhid(e.target.value)} />
          <Field label="Why are these the same person?" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="form-actions"><Button type="submit" busy={busy} disabled={reason.trim().length < 10 || uhid.trim().length < 8}>Send for approval</Button>
            <Button type="button" variant="quiet" onClick={() => setOpen(false)}>Cancel</Button></div>
        </form>
      )}
    </section>
  );
}
